// Translates raw permission-catalog entries (httpMethod + pathTemplate) into
// human-friendly labels so the Authority Management UI never shows technical
// jargon (HTTP verbs, API paths) to non-technical university staff.

const ACTION_BY_METHOD = {
  GET: "view",
  POST: "create",
  PUT: "edit",
  PATCH: "edit",
  DELETE: "delete",
};

// Primary sections shown as their own accordion group. Anything not listed
// here (staff/admin management, permissions/club-scopes meta endpoints,
// dashboard, app-config, etc.) falls into the "general" catch-all so the UI
// never leaks a raw/technical module key as a section header.
export const PRIMARY_MODULE_GROUPS = {
  clubs: { icon: "fa-users", translationKey: "clubs" },
  events: { icon: "fa-calendar-days", translationKey: "events" },
  campuses: { icon: "fa-map-location-dot", translationKey: "campuses" },
  faculties: { icon: "fa-building-columns", translationKey: "faculties" },
  departments: { icon: "fa-sitemap", translationKey: "departments" },
  finance: { icon: "fa-file-invoice-dollar", translationKey: "finance" },
};

const GENERAL_GROUP_ID = "general";

// Maps every known "httpMethod pathTemplate" pair from the permission
// catalog to a staff:permissionLabels.* translation key. The backend's raw
// `label` field (e.g. "listClubs", "List audit log entries for this
// university") is an internal/English-only identifier and must never be
// rendered to the user directly — this table is the single source of truth
// for the human-readable TR/EN text shown instead.
const PERMISSION_LABEL_KEYS = {
  "GET /university-admin/audit-log": "listAuditLog",
  "GET /university-admin/campuses": "listCampuses",
  "GET /university-admin/campuses/{id}": "getCampus",
  "POST /university-admin/campuses": "createCampus",
  "PUT /university-admin/campuses/{id}": "updateCampus",
  "DELETE /university-admin/campuses/{id}": "deleteCampus",
  "GET /university-admin/clubs": "listClubs",
  "GET /university-admin/clubs/{id}": "getClub",
  "POST /university-admin/clubs": "createClub",
  "PUT /university-admin/clubs/{id}": "updateClub",
  "DELETE /university-admin/clubs/{id}": "deleteClub",
  "GET /university-admin/departments": "listDepartments",
  "GET /university-admin/departments/{id}": "getDepartment",
  "POST /university-admin/departments": "createDepartment",
  "PUT /university-admin/departments/{id}": "updateDepartment",
  "DELETE /university-admin/departments/{id}": "deleteDepartment",
  "GET /university-admin/events": "listEvents",
  "GET /university-admin/events/{id}": "getEvent",
  "POST /university-admin/events": "createEvent",
  "PUT /university-admin/events/{id}": "updateEvent",
  "POST /university-admin/events/{id}/cancel": "cancelEvent",
  "POST /university-admin/events/{id}/approve": "approveEvent",
  "POST /university-admin/events/{id}/reject": "rejectEvent",
  "DELETE /university-admin/events/{id}": "deleteEvent",
  "GET /university-admin/events/{id}/ticket-attendants": "listTicketAttendants",
  "POST /university-admin/events/{id}/ticket-attendants": "addTicketAttendants",
  "DELETE /university-admin/events/{id}/ticket-attendants": "removeTicketAttendant",
  "GET /university-admin/faculties": "listFaculties",
  "GET /university-admin/faculties/{id}": "getFaculty",
  "POST /university-admin/faculties": "createFaculty",
  "PUT /university-admin/faculties/{id}": "updateFaculty",
  "DELETE /university-admin/faculties/{id}": "deleteFaculty",
  "POST /university-admin/clubs/{clubId}/members": "assignClubRole",
  "DELETE /university-admin/clubs/{clubId}/members/{userId}": "revokeClubRole",
  "GET /university-admin/clubs/{clubId}/members": "listElevatedMembers",
  "GET /university-admin/university/me": "getMyUniversity",
  "PUT /university-admin/university/me/login-policy": "updateLoginPolicy",
  "GET /university-admin/users": "listUsers",
  "GET /university-admin/users/{id}": "getUser",
  "POST /university-admin/users/{id}/toggle-active": "toggleUserActive",
  "POST /university-admin/users/{id}/toggle-ban": "toggleUserBan",
  "GET /university-admin/transactions": "listTransactions",
  "POST /university-admin/transactions": "createTransaction",
  "GET /university-admin/transactions/{id}": "getTransaction",
  "PUT /university-admin/transactions/{id}": "updateTransaction",
  "DELETE /university-admin/transactions/{id}": "deleteTransaction",
  "POST /university-admin/transactions/{id}/approve": "approveTransaction",
  "POST /university-admin/transactions/{id}/reject": "rejectTransaction",
  "POST /university-admin/transactions/{id}/refund": "refundTransaction",
  "GET /university-admin/transfers": "listTransfers",
  "POST /university-admin/transfers": "createTransfer",
  "GET /university-admin/transfers/{id}": "getTransfer",
  "PUT /university-admin/transfers/{id}": "updateTransfer",
  "DELETE /university-admin/transfers/{id}": "deleteTransfer",
  "GET /university-admin/transactions/transfers": "listTransfers",
  "POST /university-admin/transactions/transfers": "createTransfer",
};

export const moduleOf = (pathTemplate) =>
  (pathTemplate || "").replace(/^\/university-admin\//, "").split("/")[0] || GENERAL_GROUP_ID;

export const humanizeModuleKey = (moduleKey) =>
  moduleKey
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const primaryGroupOf = (pathTemplate) => {
  const key = moduleOf(pathTemplate);
  if (key === "transactions" || key === "transfers") return "finance";
  return PRIMARY_MODULE_GROUPS[key] ? key : GENERAL_GROUP_ID;
};

export const getGroupIcon = (groupId) => PRIMARY_MODULE_GROUPS[groupId]?.icon || "fa-building";

export const getGroupLabel = (t, groupId) => {
  if (groupId === GENERAL_GROUP_ID) return t("staff:modules.general");
  const translationKey = PRIMARY_MODULE_GROUPS[groupId]?.translationKey || groupId;
  return t(`staff:modules.${translationKey}`, { defaultValue: humanizeModuleKey(groupId) });
};

export const methodToActionKey = (httpMethod) => ACTION_BY_METHOD[httpMethod] || "manage";

export const getActionLabel = (t, httpMethod) => t(`staff:actions.${methodToActionKey(httpMethod)}`);

export const getPermissionLabel = (t, entry) => {
  const translationKey = PERMISSION_LABEL_KEYS[`${entry.httpMethod} ${entry.pathTemplate}`];
  if (translationKey) return t(`staff:permissionLabels.${translationKey}`);

  // Unmapped catalog entry (e.g. a new endpoint the backend added before the
  // front-end translation table was updated) — fall back to a generic
  // "<module> — <action>" label built from already-translated pieces instead
  // of ever showing the raw backend `label`.
  const moduleKey = moduleOf(entry.pathTemplate);
  const moduleLabel = t(`staff:modules.${PRIMARY_MODULE_GROUPS[moduleKey]?.translationKey || moduleKey}`, {
    defaultValue: humanizeModuleKey(moduleKey),
  });
  return `${moduleLabel} — ${getActionLabel(t, entry.httpMethod)}`;
};

// Groups the flat catalog into the primary sections (Clubs/Events/Campuses/
// Faculties/Departments) plus a "general" catch-all, sorted with the known
// primary groups first (in a fixed, predictable order) and "general" last.
export function groupCatalogByPrimaryModule(catalog) {
  const groups = {};
  catalog.forEach((entry) => {
    const groupId = primaryGroupOf(entry.pathTemplate);
    if (!groups[groupId]) groups[groupId] = [];
    groups[groupId].push(entry);
  });

  const order = [...Object.keys(PRIMARY_MODULE_GROUPS), GENERAL_GROUP_ID];
  return order.filter((groupId) => groups[groupId]?.length).map((groupId) => [groupId, groups[groupId]]);
}

export { GENERAL_GROUP_ID };
